import { useMemo, useState } from "react";
import {
  parseIpv6, expandIpv6, compressIpv6, ipv6Range, ipv6RangeToCidr,
  ipv4ToIpv6, ipv6ToIpv4, generateUla,
} from "../lib/ipmath";

/**
 * The IPv6 conversions people actually need, on one page instead of six thin
 * ones. All of it is arithmetic — nothing typed here leaves the browser.
 */
export function Ipv6Tools() {
  return (
    <main className="console">
      <div className="input-row">
        <span className="input-row-label">IPv6</span>
        <span className="gen-intro">
          Compress, expand, convert and subnet IPv6 addresses. Everything runs locally.
        </span>
      </div>

      <div className="body-grid ipv6-grid">
        <CompressExpand />
        <CidrRange />
        <Convert46 />
        <UlaGenerator />
      </div>
    </main>
  );
}

function Panel({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="table-panel ipv6-panel">
      <div className="panel-title">{title}</div>
      {children}
    </section>
  );
}

function Out({ label, value, bad }: { label: string; value: string; bad?: boolean }) {
  return (
    <div className="ipv6-out">
      <span className="subnet-stat-label">{label}</span>
      <code className={bad ? "ipv6-bad" : undefined}>{value}</code>
    </div>
  );
}

function CompressExpand() {
  const [v, setV] = useState("2001:0db8:0000:0000:0000:ff00:0042:8329");
  const r = useMemo(() => {
    const p = parseIpv6(v);
    if (p === null) return null;
    return { compressed: compressIpv6(p), expanded: expandIpv6(p), scope: ipv6Range(v, 128)?.scope ?? "" };
  }, [v]);
  return (
    <Panel title="COMPRESS / EXPAND">
      <label className="watch-field">
        <span className="gen-label">IPv6 address</span>
        <input value={v} onChange={(e) => setV(e.target.value)} spellCheck={false} autoCapitalize="off" autoCorrect="off" placeholder="2001:db8::ff00:42:8329" />
      </label>
      {r ? (
        <>
          <Out label="Compressed (RFC 5952)" value={r.compressed} />
          <Out label="Expanded" value={r.expanded} />
          <Out label="Scope" value={r.scope} />
        </>
      ) : (
        <Out label="Result" value="Not a valid IPv6 address" bad />
      )}
    </Panel>
  );
}

function CidrRange() {
  const [cidr, setCidr] = useState("2001:db8::/32");
  const [first, setFirst] = useState("2001:db8::");
  const [last, setLast] = useState("2001:db8:ffff:ffff:ffff:ffff:ffff:ffff");

  const range = useMemo(() => {
    const m = cidr.trim().match(/^(.+?)\s*\/\s*(\d{1,3})$/);
    if (!m) return null;
    return ipv6Range(m[1], Number(m[2]));
  }, [cidr]);

  const backToCidr = useMemo(() => ipv6RangeToCidr(first, last), [first, last]);

  return (
    <Panel title="CIDR ↔ RANGE">
      <label className="watch-field">
        <span className="gen-label">CIDR → range</span>
        <input value={cidr} onChange={(e) => setCidr(e.target.value)} spellCheck={false} autoCapitalize="off" autoCorrect="off" placeholder="2001:db8::/32" />
      </label>
      {range ? (
        <>
          <Out label="First address" value={range.first} />
          <Out label="Last address" value={range.last} />
          <Out label="Total addresses" value={range.totalAddresses} />
        </>
      ) : (
        <Out label="Result" value="Enter something like 2001:db8::/32" bad />
      )}

      <label className="watch-field ipv6-spaced">
        <span className="gen-label">Range → CIDR</span>
        <input value={first} onChange={(e) => setFirst(e.target.value)} spellCheck={false} autoCapitalize="off" autoCorrect="off" placeholder="first address" />
      </label>
      <label className="watch-field">
        <input value={last} onChange={(e) => setLast(e.target.value)} spellCheck={false} autoCapitalize="off" autoCorrect="off" placeholder="last address" aria-label="Last address" />
      </label>
      <Out
        label="CIDR"
        value={backToCidr ?? "Not a single clean CIDR block"}
        bad={!backToCidr}
      />
    </Panel>
  );
}

function Convert46() {
  const [v4, setV4] = useState("192.0.2.128");
  const [v6, setV6] = useState("::ffff:c000:280");
  const up = useMemo(() => ipv4ToIpv6(v4), [v4]);
  const down = useMemo(() => ipv6ToIpv4(v6), [v6]);
  return (
    <Panel title="IPv4 ↔ IPv6">
      <label className="watch-field">
        <span className="gen-label">IPv4 → IPv6</span>
        <input value={v4} onChange={(e) => setV4(e.target.value)} spellCheck={false} autoCapitalize="off" autoCorrect="off" placeholder="192.0.2.128" />
      </label>
      {up ? (
        <>
          <Out label="IPv4-mapped" value={up.mapped} />
          <Out label="6to4 prefix (deprecated, RFC 7526)" value={up.sixToFour} />
        </>
      ) : (
        <Out label="Result" value="Not a valid IPv4 address" bad />
      )}

      <label className="watch-field ipv6-spaced">
        <span className="gen-label">IPv6 → IPv4</span>
        <input value={v6} onChange={(e) => setV6(e.target.value)} spellCheck={false} autoCapitalize="off" autoCorrect="off" placeholder="::ffff:c000:280" />
      </label>
      {down ? (
        <>
          <Out label="Embedded IPv4" value={down.ipv4} />
          <Out label="Form" value={down.form} />
        </>
      ) : (
        <Out label="Result" value="No IPv4 address embedded in that address" bad />
      )}
      <p className="myip-note">
        Only addresses that <em>embed</em> an IPv4 address can be converted. A normal IPv6 address
        isn't a re-encoded IPv4 one — the two protocols share no address space.
      </p>
    </Panel>
  );
}

function UlaGenerator() {
  const [ula, setUla] = useState(() => generateUla());
  return (
    <Panel title="UNIQUE LOCAL PREFIX">
      <p className="gen-hint">
        A private IPv6 range for internal networks — the IPv6 answer to 192.168.x.x. RFC 4193 says
        to pick the 40-bit global ID randomly so two networks don't collide when they merge.
      </p>
      <Out label="Prefix" value={ula.prefix} />
      <Out label="Global ID" value={ula.globalId} />
      <Out label="Example host" value={ula.example} />
      <button type="button" className="watch-submit ipv6-spaced" onClick={() => setUla(generateUla())}>
        Generate another
      </button>
    </Panel>
  );
}
